import React, { useState, useMemo } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { Product, ProductFeature } from '../types';
import { Package, Plus, Edit2, Trash2, Sparkles, Link as LinkIcon, Save } from 'lucide-react';
import { ModuleInfoModal } from '../components/ModuleInfoModal';

export default function ProductManagement() {
    const { allData, activeProject, updateProduct, deleteProduct, updateProductFeature } = useAppContext();
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [showProductModal, setShowProductModal] = useState(false);
    const [showFeatureModal, setShowFeatureModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedFeature, setSelectedFeature] = useState<ProductFeature | null>(null);

    // Filter products by active project
    const projectProducts = useMemo(() => {
        if (!allData.products) return [];
        if (activeProject === 'all') return allData.products;
        return allData.products.filter(p => p.projectName === activeProject);
    }, [allData.products, activeProject]);

    // Filter features by active project
    const projectFeatures = useMemo(() => {
        if (!allData.productFeatures) return [];
        if (activeProject === 'all') return allData.productFeatures;
        return allData.productFeatures.filter(f => f.projectName === activeProject);
    }, [allData.productFeatures, activeProject]);

    // Get features for a specific product
    const getFeaturesForProduct = (productId: string) => {
        const product = projectProducts.find(p => p.id === productId);
        if (!product?.features) return [];
        return projectFeatures.filter(f => product.features?.includes(f.name));
    };

    const createNewProduct = () => {
        if (activeProject === 'all') {
            alert('Please select a specific project first');
            return;
        }
        setSelectedProduct({
            id: `PROD-${Date.now()}`,
            productLineId: '',
            name: '',
            description: '',
            partNumber: '',
            status: 'Concept',
            features: [],
            linkedCustomerReqIds: [],
            projectName: activeProject,
            epochId: allData.projectMetadata?.epochId || 0
        });
        setIsEditing(false);
        setShowProductModal(true);
    };

    const createNewFeature = () => {
        if (activeProject === 'all') {
            alert('Please select a specific project first');
            return;
        }
        setSelectedFeature({
            id: `FEAT-${Date.now()}`,
            productId: '',
            name: '',
            description: '',
            linkedCustomerReqIds: [],
            linkedBomItemIds: [],
            projectName: activeProject,
            epochId: allData.projectMetadata?.epochId || 0
        });
        setIsEditing(false);
        setShowFeatureModal(true);
    };

    const editProduct = (product: Product) => {
        setSelectedProduct({ ...product });
        setIsEditing(true);
        setShowProductModal(true);
    };

    const editFeature = (feature: ProductFeature) => {
        setSelectedFeature({ ...feature });
        setIsEditing(true);
        setShowFeatureModal(true);
    };

    const saveProduct = async (product: Product) => {
        await updateProduct(product);
        setShowProductModal(false);
        setSelectedProduct(null);
    };

    const saveFeature = async (feature: ProductFeature) => {
        await updateProductFeature(feature);
        setShowFeatureModal(false);
        setSelectedFeature(null);
    };

    const handleDeleteProduct = async (productId: string) => {
        if (confirm('Delete this product? This cannot be undone.')) {
            await deleteProduct(productId);
        }
    };

    // Note: handleDeleteFeature removed - will be added when delete feature UI is implemented

    const getStatusColor = (status: string) => {
        const colors: Record<string, string> = {
            'Concept': 'bg-gray-100 text-gray-700 border-gray-300',
            'Development': 'bg-blue-100 text-blue-700 border-blue-300',
            'Prototype': 'bg-yellow-100 text-yellow-700 border-yellow-300',
            'Production': 'bg-green-100 text-green-700 border-green-300',
            'Discontinued': 'bg-red-100 text-red-700 border-red-300'
        };
        return colors[status] || colors['Concept'];
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-card rounded-lg shadow-md p-6 border border-border">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-3xl font-bold text-text-primary flex items-center gap-3">
                            <Package className="w-8 h-8 text-primary" />
                            Product Management
                        </h1>
                        <p className="text-text-secondary mt-2">
                            Manage products and their features for {activeProject === 'all' ? 'all projects' : activeProject}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <ModuleInfoModal
                            title="Product Management"
                            description="Manage your product portfolio, features, and their relationships to customer requirements and FMEA items."
                            benefits={[
                                "Centralized product catalog",
                                "Feature-to-requirement traceability",
                                "Product lifecycle tracking"
                            ]}
                            usageInstructions={[
                                "Click 'New Product' to create a product",
                                "Add features to products",
                                "Link features to customer requirements"
                            ]}
                        />
                        <button
                            onClick={createNewProduct}
                            disabled={activeProject === 'all'}
                            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-secondary text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Plus className="w-4 h-4" />
                            New Product
                        </button>
                        <button
                            onClick={createNewFeature}
                            disabled={activeProject === 'all'}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Sparkles className="w-4 h-4" />
                            New Feature
                        </button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="bg-background p-4 rounded-lg border border-border">
                        <div className="text-sm text-text-secondary">Total Products</div>
                        <div className="text-2xl font-bold text-text-primary">{projectProducts.length}</div>
                    </div>
                    <div className="bg-background p-4 rounded-lg border border-border">
                        <div className="text-sm text-text-secondary">Total Features</div>
                        <div className="text-2xl font-bold text-text-primary">{projectFeatures.length}</div>
                    </div>
                    <div className="bg-background p-4 rounded-lg border border-border">
                        <div className="text-sm text-text-secondary">In Production</div>
                        <div className="text-2xl font-bold text-green-600">
                            {projectProducts.filter(p => p.status === 'Production').length}
                        </div>
                    </div>
                </div>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 gap-4">
                {projectProducts.map(product => {
                    const productFeatures = getFeaturesForProduct(product.id);
                    const linkedReqs = allData.customerRequirements?.filter(r =>
                        product.linkedCustomerReqIds?.includes(r.id)
                    ) || [];

                    return (
                        <div key={product.id} className="bg-card rounded-lg shadow-md border border-border overflow-hidden">
                            <div className="p-6">
                                {/* Product Header */}
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <Package className="w-6 h-6 text-primary" />
                                            <h3 className="text-xl font-bold text-text-primary">{product.name}</h3>
                                            <span className={`px-2 py-1 rounded text-xs font-semibold border ${getStatusColor(product.status)}`}>
                                                {product.status}
                                            </span>
                                        </div>
                                        <p className="text-sm text-text-secondary mb-2">{product.description}</p>
                                        <div className="flex items-center gap-4 text-xs text-text-secondary">
                                            <span className="font-mono">{product.partNumber}</span>
                                            {product.productLineId && (
                                                <span className="flex items-center gap-1">
                                                    <LinkIcon className="w-3 h-3" />
                                                    Line: {product.productLineId}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => editProduct(product)}
                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                            title="Edit Product"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteProduct(product.id)}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                                            title="Delete Product"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                {/* Features */}
                                <div className="mb-4">
                                    <h4 className="text-sm font-semibold text-text-secondary mb-2 flex items-center gap-2">
                                        <Sparkles className="w-4 h-4" />
                                        Features ({productFeatures.length})
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {productFeatures.map(feature => (
                                            <div
                                                key={feature.id}
                                                className="flex items-center gap-2 px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm border border-indigo-200 cursor-pointer hover:bg-indigo-100 transition-colors"
                                                onClick={() => editFeature(feature)}
                                            >
                                                <Sparkles className="w-3 h-3" />
                                                {feature.name}
                                            </div>
                                        ))}
                                        {productFeatures.length === 0 && (
                                            <span className="text-xs text-text-secondary italic">No features defined</span>
                                        )}
                                    </div>
                                </div>

                                {/* Linked Requirements */}
                                <div>
                                    <h4 className="text-sm font-semibold text-text-secondary mb-2 flex items-center gap-2">
                                        <LinkIcon className="w-4 h-4" />
                                        Linked Requirements ({linkedReqs.length})
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {linkedReqs.map(req => (
                                            <div
                                                key={req.id}
                                                className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs border border-blue-200"
                                            >
                                                {req.id}: {req.requirementText.substring(0, 50)}...
                                            </div>
                                        ))}
                                        {linkedReqs.length === 0 && (
                                            <span className="text-xs text-text-secondary italic">No requirements linked</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}

                {projectProducts.length === 0 && (
                    <div className="bg-card rounded-lg shadow-md border border-border p-12 text-center">
                        <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                        <p className="text-lg font-medium text-text-primary mb-2">No Products Found</p>
                        <p className="text-sm text-text-secondary">
                            {activeProject === 'all'
                                ? 'Select a project to manage products'
                                : 'Click "New Product" to create your first product'}
                        </p>
                    </div>
                )}
            </div>

            {/* Product Modal */}
            {showProductModal && selectedProduct && (
                <ProductModal
                    product={selectedProduct}
                    isEditing={isEditing}
                    onSave={saveProduct}
                    onCancel={() => {
                        setShowProductModal(false);
                        setSelectedProduct(null);
                    }}
                />
            )}

            {/* Feature Modal */}
            {showFeatureModal && selectedFeature && (
                <FeatureModal
                    feature={selectedFeature}
                    isEditing={isEditing}
                    onSave={saveFeature}
                    onCancel={() => {
                        setShowFeatureModal(false);
                        setSelectedFeature(null);
                    }}
                />
            )}
        </div>
    );
}

import { ModalWrapper } from '../components/ModalWrapper';

// Product Modal Component
function ProductModal({
    product,
    isEditing,
    onSave,
    onCancel
}: {
    product: Product;
    isEditing: boolean;
    onSave: (product: Product) => void;
    onCancel: () => void;
}) {
    const [formData, setFormData] = useState(product);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <ModalWrapper
            isOpen={true}
            onClose={onCancel}
            title={isEditing ? 'Edit Product' : 'New Product'}
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Product Name *</label>
                    <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                        placeholder="e.g., Aerator Pro - Milk Frother"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Description *</label>
                    <textarea
                        required
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none resize-none"
                        placeholder="Describe the product..."
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">Part Number *</label>
                        <input
                            type="text"
                            required
                            value={formData.partNumber}
                            onChange={(e) => setFormData({ ...formData, partNumber: e.target.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                            placeholder="e.g., AP-2025"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">Status *</label>
                        <select
                            required
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                            className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                        >
                            <option value="Concept">Concept</option>
                            <option value="Development">Development</option>
                            <option value="Prototype">Prototype</option>
                            <option value="Production">Production</option>
                            <option value="Discontinued">Discontinued</option>
                        </select>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Product Line ID</label>
                    <input
                        type="text"
                        value={formData.productLineId}
                        onChange={(e) => setFormData({ ...formData, productLineId: e.target.value })}
                        className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                        placeholder="e.g., PL-KITCHEN"
                    />
                </div>

                <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-border">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 text-sm font-medium text-text-secondary hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="px-4 py-2 text-sm font-bold text-white bg-primary hover:bg-primary-dark rounded-lg shadow-lg"
                    >
                        <Save size={16} className="inline mr-2" />
                        {isEditing ? 'Save Changes' : 'Create Product'}
                    </button>
                </div>
            </form>
        </ModalWrapper>
    );
}

// Feature Modal Component
function FeatureModal({
    feature,
    isEditing,
    onSave,
    onCancel
}: {
    feature: ProductFeature;
    isEditing: boolean;
    onSave: (feature: ProductFeature) => void;
    onCancel: () => void;
}) {
    const [formData, setFormData] = useState(feature);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onCancel}>
            <div
                className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="text-2xl font-bold text-text-primary mb-6">
                    {isEditing ? 'Edit Feature' : 'New Feature'}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">Feature Name *</label>
                        <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                            placeholder="e.g., High-torque Motor"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">Description *</label>
                        <textarea
                            required
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={3}
                            className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none resize-none"
                            placeholder="Describe the feature..."
                        />
                    </div>

                    <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-border">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-4 py-2 text-sm font-medium text-text-secondary hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-lg flex items-center gap-2"
                        >
                            <Save size={16} />
                            Save Feature
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
